import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { 
  FileText, 
  Table2, 
  Download,
  ArrowLeft,
  Loader2,
  AlertCircle,
  ChevronRight,
  Building2,
  Package,
  ExternalLink,
  Eye,
  Upload,
  AlertTriangle,
  Lock,
  Plus,
  Trash2,
  Scissors,
  ChevronDown,
  X,
  Printer,
  ArrowRightLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ExcelTable from "@/components/ExcelTable";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Color mapping for different projects
const projectColors = {
  'Amgen': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: 'bg-green-500' },
  'BMS': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: 'bg-blue-500' },
  'Hilti': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: 'bg-orange-500' },
  'Unknown': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-300', icon: 'bg-gray-500' }
};

const ProjectsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const supportParam = searchParams.get("support");
  const { canEdit, isViewer, isAdmin } = useAuth();
  const [projectSheet, setProjectSheet] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [cutList, setCutList] = useState([]);
  const [cutListLoading, setCutListLoading] = useState(false);
  const [cutListOpen, setCutListOpen] = useState(true);
  const [cutListFilter, setCutListFilter] = useState("");
  const [printBusy, setPrintBusy] = useState(false);
  // Move-row modal
  const [moveModalRow, setMoveModalRow] = useState(null);
  const [moveTargets, setMoveTargets] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [moveBusy, setMoveBusy] = useState(false);

  const openMoveModal = async (row) => {
    setMoveModalRow(row);
    setMoveTargetId("");
    try {
      const r = await axios.get(`${API}/project-sheets`);
      // Only show batches of the same sheet_type, excluding the current one.
      const list = (r.data || []).filter(
        (b) => b.id !== id && b.sheet_type === projectSheet?.sheet_type,
      );
      setMoveTargets(list);
    } catch (err) {
      toast.error("Failed to load batch list");
      setMoveTargets([]);
    }
  };

  const confirmMove = async () => {
    if (!moveModalRow || !moveTargetId) return;
    setMoveBusy(true);
    try {
      const resp = await axios.post(
        `${API}/project-sheets/${id}/move-rows`,
        { row_ids: [moveModalRow.id], target_sheet_id: moveTargetId },
      );
      const targetName = moveTargets.find((b) => b.id === moveTargetId)?.name || "target batch";
      toast.success(
        `Moved ${resp.data.moved_support_numbers.join(", ")} → ${targetName}`,
      );
      setMoveModalRow(null);
      // Refetch this batch to reflect the removed row.
      fetchProjectSheet();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to move row");
    } finally {
      setMoveBusy(false);
    }
  };

  const handlePrintPack = async () => {
    if (!id) return;
    setPrintBusy(true);
    try {
      const resp = await axios.get(`${API}/project-sheets/${id}/print-pack.pdf`, {
        responseType: "blob",
        // Cache-bust so browsers / PDF viewers never serve a stale copy —
        // e.g. after a backend orientation fix is deployed, users must get
        // the newly regenerated PDF, not the one they downloaded last week.
        params: { t: Date.now() },
        headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Open in a new tab so the browser / Android PDF viewer takes over
      // and offers the native Print action. On tablets this triggers the
      // system PDF app which has the Print → A3 flow built in.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocked → fall back to a plain download link.
        const safe = (projectSheet?.name || id).replace(/[^A-Za-z0-9_.-]+/g, "_");
        const a = document.createElement("a");
        a.href = url;
        a.download = `${safe}_print_pack.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      toast.success("Print pack ready — use your device's Print menu (choose A3)");
      // Release the URL after 60s to keep memory sensible.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error("Print pack failed:", err);
      toast.error(err.response?.data?.detail || "Failed to build print pack");
    } finally {
      setPrintBusy(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfExists, setPdfExists] = useState(true);
  const [lockedDrawings, setLockedDrawings] = useState(new Set());
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockText, setLockText] = useState("");
  const [savingLocks, setSavingLocks] = useState(false);

  // Add Drawings modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMode, setAddMode] = useState("upload"); // 'upload' | 'existing'
  const [appendFile, setAppendFile] = useState(null);
  const [appending, setAppending] = useState(false);
  const [appendResult, setAppendResult] = useState(null);
  const [availableExtractions, setAvailableExtractions] = useState([]);
  const [selectedExtractionId, setSelectedExtractionId] = useState("");
  const [reattachLoading, setReattachLoading] = useState(false);
  const rowRefs = useRef({});

  const fetchTableForRow = useCallback(async (tableId) => {
    try {
      setTableLoading(true);
      const response = await axios.get(`${API}/project-sheets/${id}/table/${tableId}`);
      setSelectedTable(response.data);
    } catch (err) {
      console.error("Table fetch error:", err);
      toast.error("Failed to load table");
    } finally {
      setTableLoading(false);
    }
  }, [id]);

  const [scannedTags, setScannedTags] = useState(() => new Set());

  const fetchProjectSheet = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/project-sheets/${id}`);
      setProjectSheet(response.data);
      
      // Check if PDF exists for this extraction
      if (response.data.extraction_id) {
        try {
          const pdfCheck = await axios.get(`${API}/extraction/${response.data.extraction_id}/pdf/exists`);
          setPdfExists(pdfCheck.data.exists);
        } catch {
          setPdfExists(false);
        }
      }
      
      // Auto-select row matching ?support=... if provided, otherwise the first
      // row. Read the param from the URL directly so this callback does NOT
      // depend on supportParam (clearing it must never trigger a refetch).
      if (response.data.rows?.length > 0) {
        const sp = new URLSearchParams(window.location.search).get("support");
        const target = sp
          ? response.data.rows.find(r => r.support_number === sp)
          : null;
        const rowToSelect = target || response.data.rows[0];
        setSelectedRow(rowToSelect);
        fetchTableForRow(rowToSelect.table_id);
      }

      // Fetch aggregated cut list in parallel (non-blocking)
      setCutListLoading(true);
      axios.get(`${API}/project-sheets/${id}/cut-list`)
        .then(r => setCutList(r.data.items || []))
        .catch(err => console.error("Cut list fetch failed:", err))
        .finally(() => setCutListLoading(false));

      // Which brackets have been scanned in Scan-IQ (by PF Short Code or SPS Tag)
      axios.get(`${API}/scan-iq/scanned-index`)
        .then(r => setScannedTags(new Set(r.data.tags || [])))
        .catch(() => {});
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.response?.data?.detail || "Failed to load project sheet");
      toast.error("Failed to load project sheet");
    } finally {
      setLoading(false);
    }
  }, [id, fetchTableForRow]);

  const handleReattachPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please select a PDF file");
      return;
    }
    
    setReattachLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await axios.post(`${API}/extraction/${projectSheet.extraction_id}/pdf/reattach`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPdfExists(true);
      toast.success("PDF successfully re-attached! View buttons are now working.");
    } catch (err) {
      console.error("Reattach error:", err);
      toast.error(err.response?.data?.detail || "Failed to re-attach PDF");
    } finally {
      setReattachLoading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const fetchLockedDrawings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locked-drawings`);
      const list = response.data?.drawings || [];
      setLockedDrawings(new Set(list));
      setLockText(list.join('\n'));
    } catch (err) {
      // Non-fatal — locked drawings are an admin feature; ignore failures
      console.warn("Failed to fetch locked drawings", err);
    }
  }, []);

  const saveLockedDrawings = async () => {
    try {
      setSavingLocks(true);
      const response = await axios.put(`${API}/locked-drawings`, { text: lockText });
      const list = response.data?.drawings || [];
      const dups = response.data?.duplicates_removed || 0;
      const input = response.data?.input_count || 0;
      setLockedDrawings(new Set(list));
      setLockText(list.join('\n'));
      if (dups > 0) {
        toast.success(
          `Saved — ${list.length} unique drawing(s) marked delivered · ${dups} duplicate(s) removed from ${input} pasted line(s)`,
          { duration: 6000 }
        );
      } else {
        toast.success(`Saved — ${list.length} drawing(s) marked delivered`);
      }
      setShowLockModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save lock list");
    } finally {
      setSavingLocks(false);
    }
  };

  const openAddDrawingsModal = async () => {
    setAppendFile(null);
    setAppendResult(null);
    setSelectedExtractionId("");
    setAddMode("upload");
    setShowAddModal(true);
    // Fetch matching-format extractions
    try {
      const r = await axios.get(`${API}/admin/documents`);
      const list = (r.data?.documents || []).filter(d => d.format === sheetType);
      setAvailableExtractions(list);
    } catch {
      setAvailableExtractions([]);
    }
  };

  const handleAppendFromExisting = async () => {
    if (!selectedExtractionId) return;
    try {
      setAppending(true);
      setAppendResult(null);
      const r = await axios.post(`${API}/project-sheets/${id}/append`, {
        extraction_id: selectedExtractionId,
      });
      setAppendResult(r.data);
      if (r.data.added > 0) {
        toast.success(`${r.data.added} drawing(s) added`);
        await fetchProjectSheet();
      } else {
        toast.info("All drawings were already in this batch");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to append drawings");
    } finally {
      setAppending(false);
    }
  };

  const handleAppendFromUpload = async () => {
    if (!appendFile) return;
    try {
      setAppending(true);
      setAppendResult(null);
      // 1) Upload PDF → extraction
      const fd = new FormData();
      fd.append('file', appendFile);
      const upResp = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newExtractionId = upResp.data?.id;
      if (!newExtractionId) throw new Error("Upload failed");
      // 2) Append to current batch
      const r = await axios.post(`${API}/project-sheets/${id}/append`, {
        extraction_id: newExtractionId,
      });
      setAppendResult(r.data);
      if (r.data.added > 0) {
        toast.success(`${r.data.added} drawing(s) added`);
        await fetchProjectSheet();
      } else {
        toast.info("All drawings were already in this batch");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Failed";
      toast.error(detail);
      setAppendResult({ error: detail });
    } finally {
      setAppending(false);
    }
  };

  useEffect(() => {
    fetchProjectSheet();
    fetchLockedDrawings();
  }, [fetchProjectSheet, fetchLockedDrawings]);

  // Get project info
  const projectRef = useMemo(() => {
    return projectSheet?.rows?.[0]?.project_ref || 'Unknown';
  }, [projectSheet]);

  const sheetType = useMemo(() => {
    return projectSheet?.sheet_type || projectSheet?.rows?.[0]?.sheet_type || 'bms';
  }, [projectSheet]);
  const isHilti = sheetType === 'hilti';

  const projectColor = projectColors[projectRef] || projectColors['Unknown'];

  // Get unique fab packs
  const uniqueFabPacks = useMemo(() => {
    if (!projectSheet?.rows) return [];
    const packs = new Set(projectSheet.rows.map(r => r.fab_pack).filter(Boolean));
    return Array.from(packs);
  }, [projectSheet]);

  // Map eng_drawing_number -> array of row PF#s sharing that S-number in
  // this batch. Used to surface a small "Secondary Drawings available"
  // hint when more than one drawing in the batch shares the same Eng #.
  const engSiblings = useMemo(() => {
    const map = {};
    for (const r of projectSheet?.rows || []) {
      const eng = (r.eng_drawing_number || "").trim();
      if (!eng) continue;
      (map[eng] = map[eng] || []).push(r.support_number);
    }
    return map;
  }, [projectSheet]);

  const handleRowSelect = (row) => {
    setSelectedRow(row);
    fetchTableForRow(row.table_id);
  };

  // When arriving via ?support=XYZ, scroll the matching row into view + flash highlight
  useEffect(() => {
    if (!supportParam || !selectedRow || selectedRow.support_number !== supportParam) return;
    const el = rowRefs.current[selectedRow.id];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-4", "ring-yellow-300");
    const t = setTimeout(() => {
      el.classList.remove("ring-4", "ring-yellow-300");
      // Drop the query param so the highlight doesn't replay on subsequent re-renders
      const next = new URLSearchParams(searchParams);
      next.delete("support");
      setSearchParams(next, { replace: true });
    }, 2500);
    return () => clearTimeout(t);
  }, [supportParam, selectedRow, searchParams, setSearchParams]);

  // Same-page deep-link: when ?support changes while the batch is already open
  // (e.g. clicking a bracket link from Scan-IQ or search), select that bracket
  // WITHOUT a refetch, so the page doesn't reload/reset.
  useEffect(() => {
    if (!supportParam || !projectSheet?.rows?.length) return;
    const target = projectSheet.rows.find(r => r.support_number === supportParam);
    if (target && target.id !== selectedRow?.id) {
      setSelectedRow(target);
      fetchTableForRow(target.table_id);
    }
  }, [supportParam, projectSheet, selectedRow, fetchTableForRow]);

  const handleDeleteRow = async (row) => {
    if (!window.confirm(`Remove drawing ${row.support_number} from this batch?\n\nOnly the batch row is removed — the source extraction stays intact.`)) return;
    try {
      await axios.delete(`${API}/project-sheets/${id}/rows/${row.id}`);
      toast.success(`Removed ${row.support_number}`);
      setProjectSheet(prev => prev ? { ...prev, rows: prev.rows.filter(r => r.id !== row.id) } : prev);
      if (selectedRow?.id === row.id) {
        setSelectedRow(null);
        setSelectedTable(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete row");
    }
  };


  const handleDateChange = async (rowId, field, value) => {
    try {
      await axios.put(`${API}/project-sheets/${id}/rows/${rowId}`, {
        [field]: value
      });
      
      // Update local state
      setProjectSheet(prev => ({
        ...prev,
        rows: prev.rows.map(r => 
          r.id === rowId ? { ...r, [field]: value } : r
        )
      }));
      
      // Update selected row if it's the one being edited
      if (selectedRow?.id === rowId) {
        setSelectedRow(prev => ({ ...prev, [field]: value }));
      }
      
      toast.success("Date saved");
    } catch (err) {
      toast.error("Failed to save date");
    }
  };

  // Toggle Cut ✓ from within the flat cut list. Persists back to the
  // underlying table's checkboxData so the per-drawing view stays in sync.
  const handleCutListToggle = async (item, newValue) => {
    const { extraction_id: eid, table_id: tid, row_index: ridx } = item;
    // Optimistic UI
    setCutList(prev => prev.map(it =>
      it.extraction_id === eid && it.table_id === tid && it.row_index === ridx
        ? { ...it, cut_complete: newValue }
        : it
    ));
    try {
      // Fetch the current table to preserve headers/rows + other checkbox state
      const tResp = await axios.get(`${API}/project-sheets/${id}/table/${tid}`);
      const table = tResp.data;
      const checkboxData = { ...(table.checkboxData || {}) };
      const key = `row_${ridx}`;
      checkboxData[key] = { ...(checkboxData[key] || {}), cutComplete: newValue };
      await axios.put(`${API}/extraction/${eid}/tables/${tid}`, {
        headers: table.headers,
        rows: table.rows,
        checkboxData,
      });
      // If the selectedTable is the one we just changed, mirror the update
      // so the right-side panel checkbox flips too.
      if (selectedTable?.id === tid) {
        setSelectedTable(prev => prev ? { ...prev, checkboxData } : prev);
      }
    } catch (err) {
      // Revert optimistic change
      setCutList(prev => prev.map(it =>
        it.extraction_id === eid && it.table_id === tid && it.row_index === ridx
          ? { ...it, cut_complete: !newValue }
          : it
      ));
      toast.error(err.response?.data?.detail || "Failed to update Cut ✓");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/project-sheets/${id}/export/excel`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project_sheet_${projectSheet?.name?.replace(/[: ]/g, '_') || 'export'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded!");
    } catch (err) {
      toast.error("Failed to export Excel");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateStr.split('T')[0];
  };

  // Filter table to match the BMS-style 4-data + 2-checkbox column layout.
  //   BMS: drop Fab Pack & CWP, leaving Model / Type / Count / Cut Length.
  //   Hilti: drop Category & CT, leaving ART / Description / QTY / LEN.
  const filteredTable = useMemo(() => {
    if (!selectedTable) return null;

    const isHiltiTable = selectedTable.format === 'hilti'
      || selectedTable.headers?.some(h => (h || '').toLowerCase() === 'len');
    const columnsToRemove = isHiltiTable
      ? ['category', 'ct']
      : ['fab pack', 'cwp'];
    const indicesToRemove = [];

    selectedTable.headers.forEach((header, idx) => {
      const hl = (header || '').toLowerCase().trim();
      if (columnsToRemove.some(col => hl === col || hl.includes(col))) {
        indicesToRemove.push(idx);
      }
    });

    const filteredHeaders = selectedTable.headers.filter((_, idx) => !indicesToRemove.includes(idx));
    const filteredRows = selectedTable.rows.map(row =>
      row.filter((_, idx) => !indicesToRemove.includes(idx))
    );

    return {
      ...selectedTable,
      headers: filteredHeaders,
      rows: filteredRows,
      col_count: filteredHeaders.length
    };
  }, [selectedTable]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-brand-600">Loading project sheet...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-brand-900 mb-2">Error Loading Project Sheet</h2>
            <p className="text-brand-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <main className="flex-1 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Table2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-2xl font-bold text-gray-900" data-testid="batch-sheet-title">
                      Batch Sheet: {projectSheet?.name}
                    </h1>
                    {isViewer && (
                      <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">
                        <Eye className="w-3 h-3 mr-1" />
                        View Only
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <Badge variant="secondary" className="bg-gray-100">
                      <FileText className="w-3 h-3 mr-1" />
                      {projectSheet?.extraction_filename}
                    </Badge>
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                      {projectSheet?.rows?.length || 0} {isHilti ? 'parts' : 'supports'}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                {isAdmin && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openAddDrawingsModal}
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      data-testid="add-drawings-btn"
                    >
                      <Plus className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Add Drawings</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLockModal(true)}
                      className="border-amber-200 text-amber-700 hover:bg-amber-50"
                      data-testid="manage-locks-btn"
                    >
                      <Lock className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Delivered Drawings</span>
                      {lockedDrawings.size > 0 && (
                        <Badge variant="secondary" className="ml-1 sm:ml-2 bg-amber-100 text-amber-800">
                          {lockedDrawings.size}
                        </Badge>
                      )}
                    </Button>
                  </>
                )}
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handlePrintPack}
                  disabled={printBusy || !projectSheet?.rows?.length}
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="print-pack-btn"
                  title="Consolidate all drawings into one A3 PDF for printing"
                >
                  {printBusy
                    ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                    : <Printer className="w-4 h-4 sm:mr-2" />}
                  <span className="hidden sm:inline">{printBusy ? "Building…" : "Print (A3)"}</span>
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleExportExcel}
                  className="border-green-200 text-green-700 hover:bg-green-50"
                  data-testid="export-batch-excel-btn"
                >
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Export Excel</span>
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/results/${projectSheet?.extraction_id}`)}
                  data-testid="view-extraction-btn"
                  title="View Full Extraction"
                >
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">View Full Extraction</span>
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/")}
                  data-testid="back-home-btn"
                >
                  <ArrowLeft className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Home</span>
                </Button>
              </div>
            </div>
          </div>

          {/* PDF Missing Warning Banner */}
          {!pdfExists && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 animate-fade-in" data-testid="pdf-missing-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800">PDF File Missing</h3>
                  <p className="text-amber-700 text-sm mt-1">
                    The original PDF file for this batch is no longer available. The "View" buttons won't work until you re-upload the PDF.
                    <strong className="block mt-1">Your batch progress (dates, checkboxes) will be preserved.</strong>
                  </p>
                  {isAdmin && (
                    <div className="mt-3">
                      <label className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg cursor-pointer transition-colors">
                        {reattachLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {reattachLoading ? "Uploading..." : "Re-upload PDF"}
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={handleReattachPdf}
                          disabled={reattachLoading}
                          data-testid="reattach-pdf-input"
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cut List — flat batch-wide list, sorted longest first */}
          <div className="bg-white rounded-xl border border-brand-200 shadow-soft overflow-hidden mb-6 animate-fade-in" data-testid="cut-list-section">
            <button
              type="button"
              className="w-full bg-purple-50 border-b border-purple-200 px-4 py-3 flex items-center gap-3 hover:bg-purple-100/60 transition-colors"
              onClick={() => setCutListOpen(o => !o)}
              data-testid="cut-list-toggle"
            >
              <div className="w-9 h-9 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-heading font-bold text-purple-900">Cut List</h3>
                <p className="text-xs text-purple-700/80">
                  {cutListLoading ? "Loading…" : (
                    <>
                      {cutList.length} cuts across {projectSheet?.rows?.length || 0} drawing{(projectSheet?.rows?.length || 0) === 1 ? "" : "s"} · sorted longest first
                      {cutList.length > 0 && (
                        <> · <strong className="text-purple-900">{cutList.filter(x => x.cut_complete).length}</strong> of <strong className="text-purple-900">{cutList.length}</strong> cut</>
                      )}
                    </>
                  )}
                </p>
              </div>
              {cutListOpen
                ? <ChevronDown className="w-5 h-5 text-purple-700" />
                : <ChevronRight className="w-5 h-5 text-purple-700" />}
            </button>
            {cutListOpen && (
              <>
                <div className="border-b border-brand-200 px-4 py-2 flex items-center gap-2">
                  <div className="relative flex-1 max-w-md">
                    <Input
                      value={cutListFilter}
                      onChange={(e) => setCutListFilter(e.target.value)}
                      placeholder="Filter by Drawing #, Eng #, ART or description…"
                      className="h-7 text-xs pr-7"
                      data-testid="cut-list-filter"
                    />
                    {cutListFilter && (
                      <button
                        type="button"
                        onClick={() => setCutListFilter("")}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-brand-400 hover:text-brand-700"
                        data-testid="cut-list-filter-clear"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-[380px] overflow-y-auto">
                  <table className="w-full text-xs" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                    <thead className="sticky top-0 bg-purple-50 z-10">
                      <tr className="text-left text-purple-800 border-b border-purple-200">
                        <th className="px-2 py-2 font-semibold">Drawing</th>
                        <th className="px-2 py-2 font-semibold">Eng #</th>
                        <th className="px-2 py-2 font-semibold">ART</th>
                        <th className="px-2 py-2 font-semibold">Description</th>
                        <th className="px-2 py-2 font-semibold text-center">QTY</th>
                        <th className="px-2 py-2 font-semibold text-right">LEN</th>
                        <th className="px-2 py-2 font-semibold text-center w-14">Cut ✓</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cutList
                        .filter(it => {
                          const q = cutListFilter.trim().toLowerCase();
                          if (!q) return true;
                          return [it.drawing_number, it.eng_drawing_number, it.art, it.description, String(it.length)]
                            .some(v => String(v || "").toLowerCase().includes(q));
                        })
                        .map((it, i) => (
                          <tr
                            key={`${it.extraction_id}-${it.table_id}-${it.row_index}`}
                            className={`border-b border-brand-100 ${it.cut_complete ? "bg-green-50/40" : "hover:bg-purple-50/30"} transition-colors`}
                          >
                            <td className="px-2 py-1.5 text-brand-800 font-medium whitespace-nowrap">
                              <button
                                type="button"
                                className="text-blue-700 hover:underline"
                                onClick={() => {
                                  const targetRow = projectSheet?.rows?.find(r => r.support_number === it.drawing_number);
                                  if (targetRow) {
                                    setSelectedRow(targetRow);
                                    fetchTableForRow(targetRow.table_id);
                                  }
                                }}
                                title="Open in Table Details"
                                data-testid={`cut-list-jump-${i}`}
                              >
                                {it.drawing_number}
                              </button>
                            </td>
                            <td className="px-2 py-1.5 text-brand-700 whitespace-nowrap">{it.eng_drawing_number || "—"}</td>
                            <td className="px-2 py-1.5 text-brand-700 whitespace-nowrap">{it.art}</td>
                            <td className="px-2 py-1.5 text-brand-700">{it.description}</td>
                            <td className="px-2 py-1.5 text-center text-brand-700">{it.qty}</td>
                            <td className="px-2 py-1.5 text-right text-brand-900 font-semibold">{Number.isInteger(it.length) ? it.length : it.length.toFixed(1)}</td>
                            <td className="px-2 py-1.5 text-center">
                              <input
                                type="checkbox"
                                checked={it.cut_complete}
                                onChange={(e) => handleCutListToggle(it, e.target.checked)}
                                disabled={!canEdit}
                                className="w-4 h-4 accent-green-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                data-testid={`cut-list-checkbox-${i}`}
                              />
                            </td>
                          </tr>
                        ))}
                      {!cutListLoading && cutList.length === 0 && (
                        <tr><td colSpan={7} className="px-2 py-6 text-center text-brand-500">No cut-length items in this batch.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Split View */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in delay-100">
            {/* Left Panel - Project Rows */}
            <div className="bg-white rounded-xl border border-brand-200 shadow-soft overflow-hidden">
              {/* Project Info Header */}
              <div className={`${projectColor.bg} border-b ${projectColor.border} px-4 py-3`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-8 h-8 ${projectColor.icon} rounded-lg flex items-center justify-center`}>
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className={`font-heading font-bold text-lg ${projectColor.text}`}>
                    {projectRef}
                  </h2>
                </div>
                {uniqueFabPacks.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Package className="w-4 h-4 text-brand-500" />
                    <span className="text-sm text-brand-600 mr-2">Fab Packs:</span>
                    {uniqueFabPacks.map((fp, i) => (
                      <Badge key={i} className="bg-amber-100 text-amber-800 border-amber-200">
                        {fp}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Table Header */}
              <div className="bg-purple-50 border-b border-brand-200 px-4 py-2">
                <p className="text-sm text-brand-600">
                  Click a row to view the corresponding table
                </p>
              </div>
              
              <div className="h-[550px] overflow-auto">
                <table className="border-collapse min-w-full" data-testid="project-table" style={{ fontFamily: '"Courier New", Courier, monospace' }}>
                    <thead className="sticky top-0 bg-brand-100 z-10">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-brand-700 border-b">
                          {isHilti ? 'Drawing #' : 'Support #'}
                        </th>
                        {isHilti && (
                          <th className="px-2 py-2 text-center text-xs font-semibold text-brand-700 border-b">Eng Drawing #</th>
                        )}
                        <th className="px-2 py-2 text-center text-xs font-semibold text-brand-700 border-b">Date Cut</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-brand-700 border-b">Date Fabbed</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-brand-700 border-b whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectSheet?.rows?.map((row, idx) => {
                        const isLocked = lockedDrawings.has(row.support_number);
                        const rowCanEdit = canEdit && (isAdmin || !isLocked);
                        const isScanned =
                          scannedTags.has((row.support_number || "").trim().toUpperCase()) ||
                          scannedTags.has((row.eng_drawing_number || "").trim().toUpperCase());
                        return (
                        <tr 
                          key={row.id}
                          ref={(el) => { rowRefs.current[row.id] = el; }}
                          className={`cursor-pointer transition-colors border-b border-brand-100 ${
                            selectedRow?.id === row.id 
                              ? 'bg-purple-100 border-l-4 border-l-purple-500' 
                              : isLocked
                                ? 'bg-red-200 hover:bg-red-300 border-l-[6px] border-l-red-700 ring-1 ring-inset ring-red-300'
                                : 'hover:bg-brand-50'
                          }`}
                          onClick={() => handleRowSelect(row)}
                          data-testid={`project-row-${idx}`}
                        >
                          <td className="px-2 py-3 text-sm text-brand-700 font-medium whitespace-nowrap align-top">
                            <div className="flex items-center">
                              {isHilti && (
                                <span className="mr-2 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded text-[10px] font-sans font-medium bg-orange-100 text-orange-700">
                                  {idx + 1}
                                </span>
                              )}
                              {isLocked && (
                                <Lock
                                  className="inline-block w-4 h-4 mr-1 text-red-800 align-text-bottom"
                                  aria-label="Delivered"
                                  data-testid={`lock-icon-${idx}`}
                                />
                              )}
                              <span className={isLocked ? 'text-red-900 font-bold' : ''}>
                                {row.support_number}
                              </span>
                              {(isLocked || isScanned) && (
                                <span className="ml-2 inline-flex flex-col items-start gap-0.5">
                                  {isLocked && (
                                    <span
                                      className="inline-flex items-center px-1 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-red-700 text-white leading-tight"
                                      data-testid={`lock-badge-${idx}`}
                                    >
                                      Delivered
                                    </span>
                                  )}
                                  {isScanned && (
                                    <span
                                      className="inline-flex items-center px-1 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-blue-600 text-white leading-tight"
                                      data-testid={`scanned-badge-${idx}`}
                                    >
                                      Scanned
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                            {row.max_len != null && (() => {
                              const cls = row.max_len > 1500
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : row.max_len >= 500
                                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                                  : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                              return (
                                <span
                                  className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-sans font-medium border ${cls}`}
                                  title="Longest LEN in this drawing's parts table"
                                  data-testid={`max-len-${idx}`}
                                >
                                  {Number.isInteger(row.max_len) ? row.max_len : row.max_len.toFixed(1)}mm
                                </span>
                              );
                            })()}
                            {(() => {
                              const eng = (row.eng_drawing_number || "").trim();
                              if (!eng) return null;
                              const siblings = (engSiblings[eng] || []).filter(s => s !== row.support_number);
                              if (siblings.length === 0) return null;
                              return (
                                <div
                                  className="mt-0.5 text-[9px] font-sans italic text-blue-600/80 leading-tight"
                                  title={`Same Eng #: ${siblings.join(", ")}`}
                                  data-testid={`secondary-drawings-hint-${idx}`}
                                >
                                  Secondary Drawings available
                                </div>
                              );
                            })()}
                          </td>
                          {isHilti && (
                            <td className="px-2 py-3 text-xs text-brand-700 whitespace-nowrap text-center">
                              {row.eng_drawing_number || <span className="text-gray-300">—</span>}
                            </td>
                          )}
                          <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="date"
                              value={formatDate(row.date_cut)}
                              onChange={(e) => rowCanEdit && handleDateChange(row.id, 'date_cut', e.target.value)}
                              disabled={!rowCanEdit}
                              title={isLocked && !isAdmin ? "DELIVERED — read only" : undefined}
                              className={`w-[112px] px-1.5 py-1 text-xs border border-brand-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${rowCanEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'}`}
                              style={{ fontFamily: '"Courier New", Courier, monospace' }}
                              data-testid={`date-cut-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="date"
                              value={formatDate(row.date_fabbed)}
                              onChange={(e) => rowCanEdit && handleDateChange(row.id, 'date_fabbed', e.target.value)}
                              disabled={!rowCanEdit}
                              title={isLocked && !isAdmin ? "DELIVERED — read only" : undefined}
                              className={`w-[112px] px-1.5 py-1 text-xs border border-brand-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${rowCanEdit ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'}`}
                              style={{ fontFamily: '"Courier New", Courier, monospace' }}
                              data-testid={`date-fabbed-${idx}`}
                            />
                          </td>
                          <td className="px-2 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-1">
                              {pdfExists ? (
                                <a
                                  href={`/api/extraction/${row.extraction_id || projectSheet?.extraction_id}/pdf/page/${row.page_number}/view?title=${encodeURIComponent(row.support_number)}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center justify-center w-7 h-7 text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors"
                                  title="View PDF"
                                  data-testid={`view-pdf-${idx}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              ) : (
                                <span 
                                  className="inline-flex items-center justify-center w-7 h-7 text-gray-400 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed"
                                  title="PDF file missing - Re-upload to enable"
                                  data-testid={`view-pdf-disabled-${idx}`}
                                >
                                  <AlertTriangle className="w-3.5 h-3.5" />
                                </span>
                              )}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => openMoveModal(row)}
                                  className="inline-flex items-center justify-center w-7 h-7 text-indigo-600 hover:text-white hover:bg-indigo-600 border border-indigo-200 hover:border-indigo-600 rounded-md transition-colors"
                                  title={`Move ${row.support_number} to another batch`}
                                  data-testid={`move-row-${idx}`}
                                >
                                  <ArrowRightLeft className="w-3.5 h-3.5" />
                                </button>
                              )}
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteRow(row)}
                                  className="inline-flex items-center justify-center w-7 h-7 text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 rounded-md transition-colors"
                                  title={`Remove ${row.support_number} from this batch`}
                                  data-testid={`delete-row-${idx}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
            </div>

            {/* Right Panel - Selected Table */}
            <div className="bg-white rounded-xl border border-brand-200 shadow-soft overflow-hidden">
              <div className="bg-blue-50 border-b border-brand-200 px-4 py-3 flex items-center justify-between">
                <div>
                  <h2 className="font-heading font-semibold text-brand-900">
                    Table Details
                  </h2>
                  {selectedRow && (
                    <p className="text-sm text-brand-500 mt-1">
                      Page {selectedRow.page_number} • {selectedRow.row_count} rows
                    </p>
                  )}
                </div>
                {selectedRow && (
                  <Badge variant="outline" className="text-blue-700 border-blue-300">
                    <ChevronRight className="w-3 h-3 mr-1" />
                    {selectedRow.support_number}
                  </Badge>
                )}
              </div>
              
              <div className="h-[600px] overflow-y-auto">
                {tableLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  </div>
                ) : filteredTable ? (
                  <div className="p-4">
                    <ExcelTable
                      table={{
                        ...filteredTable,
                        // Pass the original table ID for saving
                        originalId: selectedTable?.id
                      }}
                      tableIndex={0}
                      onExportCSV={() => {}}
                      isEditable={canEdit && (isAdmin || !lockedDrawings.has(selectedRow?.support_number))}
                      canRename={isAdmin}
                      onRenameTable={async (tableId, newName) => {
                        const targetExtractionId = selectedRow?.extraction_id || projectSheet?.extraction_id;
                        try {
                          await axios.patch(
                            `${API}/extraction/${targetExtractionId}/tables/${selectedTable?.id}/name`,
                            { name: newName },
                          );
                          // Update local state so the renamed title sticks
                          setSelectedTable(prev => prev ? { ...prev, table_name: newName, name_overridden: true } : prev);
                          toast.success("Table renamed");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to rename table");
                          throw err;
                        }
                      }}
                      onSaveTable={async (tableId, updatedData) => {
                        // Save to the row's source extraction (might differ
                        // from the batch's original extraction if drawings
                        // were appended from a different PDF).
                        const targetExtractionId = selectedRow?.extraction_id || projectSheet?.extraction_id;
                        try {
                          await axios.put(
                            `${API}/extraction/${targetExtractionId}/tables/${selectedTable?.id}`,
                            {
                              headers: selectedTable?.headers,
                              rows: selectedTable?.rows,
                              checkboxData: updatedData.checkboxData
                            }
                          );
                          toast.success("Saved");
                          // Update local state
                          setSelectedTable(prev => ({
                            ...prev,
                            checkboxData: updatedData.checkboxData
                          }));
                        } catch (err) {
                          console.error("Save error:", err);
                          toast.error(err.response?.data?.detail || "Failed to save");
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-brand-400">
                    <div className="text-center">
                      <Table2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Select a row to view table details</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Add Drawings Modal — admin only */}
      {showAddModal && isAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !appending && setShowAddModal(false)}
          data-testid="add-drawings-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-emerald-50 border-b border-emerald-200 px-5 py-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-700" />
              <h2 className="font-heading font-semibold text-emerald-900">Add Drawings to Batch</h2>
              <span className="ml-auto text-xs text-emerald-700 font-mono">{sheetType.toUpperCase()}</span>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">
                Drawings already in this batch will be skipped. Only <strong>{sheetType}</strong>-format extractions can be merged.
              </p>

              {/* Mode tabs */}
              <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
                <button
                  type="button"
                  onClick={() => setAddMode("upload")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${addMode === "upload" ? "bg-white shadow-sm text-emerald-700" : "text-gray-600 hover:text-gray-900"}`}
                  data-testid="add-mode-upload"
                >
                  Upload PDF
                </button>
                <button
                  type="button"
                  onClick={() => setAddMode("existing")}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${addMode === "existing" ? "bg-white shadow-sm text-emerald-700" : "text-gray-600 hover:text-gray-900"}`}
                  data-testid="add-mode-existing"
                >
                  Pick existing extraction
                </button>
              </div>

              {addMode === "upload" ? (
                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">PDF file</span>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={(e) => setAppendFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 cursor-pointer"
                      data-testid="append-file-input"
                    />
                  </label>
                  {appendFile && (
                    <div className="text-xs text-gray-600">
                      Selected: <strong>{appendFile.name}</strong> ({(appendFile.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-700 mb-1">
                      Existing extraction ({availableExtractions.length} matching {sheetType})
                    </span>
                    <select
                      value={selectedExtractionId}
                      onChange={(e) => setSelectedExtractionId(e.target.value)}
                      className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="append-extraction-select"
                    >
                      <option value="">— Select an extraction —</option>
                      {availableExtractions.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.filename} · {d.table_count} drawings · {(d.file_size / 1024).toFixed(0)} KB
                        </option>
                      ))}
                    </select>
                  </label>
                  {availableExtractions.length === 0 && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      No matching extractions found. Upload a new PDF using the other tab.
                    </p>
                  )}
                </div>
              )}

              {appendResult && (
                <div className={`text-sm rounded-md p-3 ${appendResult.error ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-800'}`} data-testid="append-result">
                  {appendResult.error ? (
                    <span>Error: {appendResult.error}</span>
                  ) : (
                    <div className="space-y-1">
                      <div><strong>{appendResult.added}</strong> drawing(s) added, <strong>{appendResult.skipped}</strong> skipped (already in batch).</div>
                      {appendResult.skipped > 0 && appendResult.skipped_drawings?.length > 0 && (
                        <div className="text-xs text-gray-600 font-mono">
                          Skipped: {appendResult.skipped_drawings.slice(0, 8).join(", ")}{appendResult.skipped_drawings.length > 8 ? `, +${appendResult.skipped_drawings.length - 8} more` : ""}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                disabled={appending}
                data-testid="add-cancel-btn"
              >
                Close
              </Button>
              <Button
                onClick={addMode === "upload" ? handleAppendFromUpload : handleAppendFromExisting}
                disabled={appending || (addMode === "upload" ? !appendFile : !selectedExtractionId)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="add-submit-btn"
              >
                {appending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {addMode === "upload" ? "Uploading & merging..." : "Merging..."}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Drawings
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Move-Row Modal — admin only */}
      {moveModalRow && isAdmin && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="move-row-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-heading font-bold text-lg text-gray-900 mb-1">
              Move drawing
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Move <span className="font-mono font-semibold">{moveModalRow.support_number}</span>
              {moveModalRow.eng_drawing_number && (
                <> (<span className="font-mono text-gray-500">{moveModalRow.eng_drawing_number}</span>)</>
              )}
              {" "}to another batch. All part checkboxes, cut/fab dates and page refs will be preserved.
            </p>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Target batch
            </label>
            <select
              value={moveTargetId}
              onChange={(e) => setMoveTargetId(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-2 bg-white text-gray-800 mb-4"
              data-testid="move-target-select"
            >
              <option value="">Select a batch…</option>
              {moveTargets.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name || b.id}
                </option>
              ))}
            </select>
            {moveTargets.length === 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-4">
                No other batches of the same type ({projectSheet?.sheet_type}) available.
              </p>
            )}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setMoveModalRow(null)}
                disabled={moveBusy}
                data-testid="move-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmMove}
                disabled={moveBusy || !moveTargetId}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="move-confirm-btn"
              >
                {moveBusy ? "Moving…" : "Move"}
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Locked Drawings Modal — admin only */}
      {showLockModal && isAdmin && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowLockModal(false)}
          data-testid="lock-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-amber-50 border-b border-amber-200 px-5 py-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-700" />
              <h2 className="font-heading font-semibold text-amber-900">Delivered Drawings</h2>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600">
                Enter one drawing/support number per line. Drawings marked as
                DELIVERED cannot be edited by regular users (Date Cut, Date
                Fabbed, and parts checkboxes all become read-only). Admins are
                always able to edit. Joint & Cut counts are unaffected.
              </p>
              <textarea
                value={lockText}
                onChange={(e) => setLockText(e.target.value)}
                rows={12}
                placeholder={"PF-0018\nPF-0019\nPF-0020"}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="lock-textarea"
              />
              <p className="text-xs text-gray-500">
                Whitespace and commas are also accepted as separators.
                Currently delivered: <strong>{lockedDrawings.size}</strong>.
              </p>
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowLockModal(false)}
                disabled={savingLocks}
                data-testid="lock-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={saveLockedDrawings}
                disabled={savingLocks}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="lock-save-btn"
              >
                {savingLocks ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Save Delivery List
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
